import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { GraduationCap, Search, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';

const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '';

const CELL = {
  scaduto: 'bg-red-100 text-red-800 border-red-200',
  in_scadenza: 'bg-amber-100 text-amber-800 border-amber-200',
  valido: 'bg-green-100 text-green-800 border-green-200',
};

export default function TrainingOverviewPage() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [ovRes, tRes] = await Promise.all([
          api.get('/employee-courses/overview'),
          api.get('/employee-courses/types'),
        ]);
        setData(ovRes.data || []);
        setTypes(tRes.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const typeLabel = (v) => types.find(t => t.value === v)?.label || v || '-';
  const keyOf = (c) => c.course_type || c.course_name || 'altro';

  // colonne = tipi corso presenti nei dati, ordinati come da listino tipi
  const cols = useMemo(() => {
    const present = new Set();
    data.forEach(e => (e.courses || []).forEach(c => present.add(keyOf(c))));
    const ordered = types.filter(t => present.has(t.value)).map(t => ({ value: t.value, label: t.label }));
    const extras = [...present].filter(v => !types.some(t => t.value === v)).map(v => ({ value: v, label: v }));
    return [...ordered, ...extras];
  }, [data, types]);

  const filtered = useMemo(() => {
    if (!search) return data;
    const q = search.toLowerCase();
    return data.filter(e => (e.employee_name || '').toLowerCase().includes(q));
  }, [data, search]);

  const counts = useMemo(() => {
    const all = data.flatMap(e => e.courses || []);
    return {
      scaduto: all.filter(c => c.status === 'scaduto').length,
      in_scadenza: all.filter(c => c.status === 'in_scadenza').length,
      valido: all.filter(c => c.status === 'valido').length,
    };
  }, [data]);

  const cellFor = (emp, typeVal) => {
    const cs = (emp.courses || []).filter(c => keyOf(c) === typeVal);
    if (!cs.length) return null;
    return cs.slice().sort((a, b) => (b.expiry_date || '').localeCompare(a.expiry_date || ''))[0];
  };

  return (
    <div className="space-y-4" data-testid="training-overview-page">
      <div className="flex items-center gap-2">
        <GraduationCap size={24} className="text-[#1a4b8c]" />
        <div>
          <h1 className="text-xl font-bold text-slate-800">Scadenze Formazione</h1>
          <p className="text-sm text-slate-500">Matrice dipendenti × corsi. Verde valido, giallo in scadenza, rosso scaduto.</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="border-red-200"><CardContent className="p-3 flex items-center gap-2"><AlertTriangle className="text-red-600" size={20} /><div><p className="text-xs text-red-700">Scaduti</p><p className="text-2xl font-bold text-red-800" data-testid="count-scaduto">{counts.scaduto}</p></div></CardContent></Card>
        <Card className="border-amber-200"><CardContent className="p-3 flex items-center gap-2"><Clock className="text-amber-600" size={20} /><div><p className="text-xs text-amber-700">In scadenza</p><p className="text-2xl font-bold text-amber-800" data-testid="count-in-scadenza">{counts.in_scadenza}</p></div></CardContent></Card>
        <Card className="border-green-200"><CardContent className="p-3 flex items-center gap-2"><CheckCircle2 className="text-green-600" size={20} /><div><p className="text-xs text-green-700">Validi</p><p className="text-2xl font-bold text-green-800" data-testid="count-valido">{counts.valido}</p></div></CardContent></Card>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-2.5 top-2.5 text-slate-400" />
        <Input className="pl-8" placeholder="Cerca dipendente..." value={search} onChange={e => setSearch(e.target.value)} data-testid="training-search" />
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Caricamento...</p>
      ) : cols.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Nessun corso registrato.</CardContent></Card>
      ) : (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="sticky left-0 z-10 bg-slate-50 text-left px-3 py-2 font-semibold text-slate-700 border-b border-r border-slate-200 min-w-[180px]">Dipendente</th>
                  {cols.map(col => (
                    <th key={col.value} className="px-2 py-2 text-center font-semibold text-slate-600 border-b border-slate-200 text-xs min-w-[110px] align-bottom">{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(emp => (
                  <tr key={emp.employee_id} className="hover:bg-slate-50/60" data-testid={`emp-row-${emp.employee_id}`}>
                    <td className="sticky left-0 z-10 bg-white px-3 py-2 border-b border-r border-slate-200 font-medium text-slate-800 cursor-pointer hover:text-[#1a4b8c] whitespace-nowrap" onClick={() => navigate(`/employees/${emp.employee_id}?tab=corsi`)} data-testid={`emp-name-${emp.employee_id}`}>{emp.employee_name || '-'}</td>
                    {cols.map(col => {
                      const c = cellFor(emp, col.value);
                      return (
                        <td key={col.value} className="px-2 py-1.5 border-b border-slate-100 text-center" data-testid={`cell-${emp.employee_id}-${col.value}`}>
                          {c ? (
                            <div className={`inline-flex flex-col items-center rounded-md border px-2 py-1 ${CELL[c.status] || CELL.valido}`}>
                              <span className="text-[11px] font-bold leading-none">SÌ</span>
                              <span className="text-[10px] leading-tight mt-0.5">{formatDate(c.expiry_date) || 'senza scad.'}</span>
                            </div>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap gap-3 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-100 border border-green-300 inline-block" /> Valido</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300 inline-block" /> In scadenza</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 border border-red-300 inline-block" /> Scaduto</span>
        <span className="inline-flex items-center gap-1"><span className="text-slate-300">—</span> Corso non presente</span>
      </div>
    </div>
  );
}
